import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Edit, Trash2, Lock, Unlock, Clock,
  FileText, Video, BookOpen, CheckCircle, AlertCircle, Settings,
  Save, ChevronDown, ChevronUp, ArrowRight, RefreshCw,
  Download, Eye
} from 'lucide-react';
import trainingService from '../../../api/services/trainingService';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import toast from 'react-hot-toast';

const ModulesLessons = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();

  // File validation function
  const validateFile = (file, allowedTypes, maxSizeMB = 100) => {
    const errors = [];

    // Check file size (convert MB to bytes)
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      errors.push(`File size must be less than ${maxSizeMB}MB`);
    }

    // Check file type
    if (allowedTypes && allowedTypes.length > 0) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const mimeType = file.type;

      const isAllowedByExtension = allowedTypes.some(type =>
        type.toLowerCase() === fileExtension
      );

      const isAllowedByMimeType = allowedTypes.some(type => {
        const mimeMap = {
          'pdf': 'application/pdf',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'mp4': 'video/mp4',
          'webm': 'video/webm',
          'ogg': 'video/ogg',
          'zip': 'application/zip',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'txt': 'text/plain',
          'ppt': 'application/vnd.ms-powerpoint',
          'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        };
        return mimeMap[type] === mimeType;
      });

      if (!isAllowedByExtension && !isAllowedByMimeType) {
        errors.push(`File type not allowed. Allowed types: ${allowedTypes.join(', ')}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  };

  // State management
  const [loading, setLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const initializationRef = useRef(false);

  const [showAddModule, setShowAddModule] = useState(false);
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const currentModuleIdRef = useRef(null);
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [moduleLessons, setModuleLessons] = useState({}); // Store lessons for each module
  const [lessonDetailsModal, setLessonDetailsModal] = useState({
    show: false,
    lesson: null,
    module: null
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const [loadingLessons, setLoadingLessons] = useState({}); // Track loading state for each module
  const [uploadingFiles, setUploadingFiles] = useState({
    video: false,
    doc: false
  });

  const [deletingFiles, setDeletingFiles] = useState({
    video: false,
    doc: false
  });
  const initialFileDeleteState = {
    show: false,
    fileType: null,
    fileData: null,
    onSuccess: null
  };
  const [fileDeleteConfirm, setFileDeleteConfirm] = useState(initialFileDeleteState);
  const [uploadProgress, setUploadProgress] = useState({
    video: 0,
    doc: 0
  });
  const [dragActive, setDragActive] = useState({
    video: false,
    doc: false
  });

  // Form states - simplified for production
  // Note: Modules are organizational containers only - all content (duration, files, videos) should be in lessons
  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    order: 0,
    isLocked: false
  });

  const [lessonForm, setLessonForm] = useState({
    title: '',
    description: '',
    type: 'video',
    content: '',
    duration: '',
    isGraded: false,
    passScore: 80,
    order: '',
    prerequisites: [],
    isLocked: false,
    videoFile: null,
    docFile: null,
    youtubeUrl: '',
    externalLinks: []
  });

  // Store extracted video duration for validation
  const [extractedVideoDuration, setExtractedVideoDuration] = useState(null);

  // Validation functions
  const validateModuleForm = (form) => {
    const errors = {};

    // Validate title
    if (!form.title || form.title.trim().length === 0) {
      errors.title = 'Module title is required';
    } else if (form.title.trim().length < 3) {
      errors.title = 'Module title must be at least 3 characters';
    } else if (form.title.trim().length > 100) {
      errors.title = 'Module title must be less than 100 characters';
    }

    // Validate description (required)
    if (!form.description || form.description.trim().length === 0) {
      errors.description = 'Module description is required';
    } else if (form.description.trim().length < 10) {
      errors.description = 'Module description must be at least 10 characters';
    } else if (form.description.trim().length > 500) {
      errors.description = 'Module description must be less than 500 characters';
    }

    // Note: content, duration, files removed - modules are organizational containers only
    // All content should be in lessons

    // Validate order (allow empty, but if provided must be valid)
    if (form.order !== '' && form.order !== null && form.order !== undefined) {
      const orderNum = Number(form.order);
      if (isNaN(orderNum) || !Number.isInteger(orderNum) || orderNum < 1) {
        errors.order = 'Order must be at least 1';
      } else if (orderNum > 1000) {
        errors.order = 'Order must be less than 1000';
      }
    }

    return errors;
  };

  // Form reset functions (following course creation pattern)
  const resetModuleForm = () => {
    setValidationErrors({});
    setError(null);
    setEditingModule(null);
    setModuleForm({
      title: '',
      description: '',
      order: 0,
      isLocked: false
    });
  };

  const resetLessonForm = () => {
    setExtractedVideoDuration(null); // Clear extracted duration when resetting form
    setValidationErrors({});
    setError(null);
    setEditingLesson(null);
    setSelectedModule(null);
    setLessonForm({
      title: '',
      description: '',
      type: 'video',
      content: '',
      duration: '',
      order: 0,
      isGraded: false,
      passScore: 80,
      isLocked: false,
      prerequisites: [],
      videoFile: null,
      docFile: null,
      youtubeUrl: '',
      externalLinks: []
    });
  };

  const validateLessonForm = (form) => {
    const errors = {};

    // Validate title
    if (!form.title || form.title.trim().length === 0) {
      errors.title = 'Lesson title is required';
    } else if (form.title.trim().length < 3) {
      errors.title = 'Lesson title must be at least 3 characters';
    } else if (form.title.trim().length > 100) {
      errors.title = 'Lesson title must be less than 100 characters';
    }

    // Validate description
    if (!form.description || form.description.trim().length === 0) {
      errors.description = 'Lesson description is required';
    } else if (form.description.trim().length < 3) {
      errors.description = 'Lesson description must be at least 3 characters';
    } else if (form.description.trim().length > 500) {
      errors.description = 'Lesson description must be less than 500 characters';
    }

    // Validate content
    if (!form.content || form.content.trim().length === 0) {
      errors.content = 'Lesson content is required';
    } else if (form.content.trim().length < 3) {
      errors.content = 'Lesson content must be at least 3 characters';
    } else if (form.content.trim().length > 2000) {
      errors.content = 'Lesson content must be less than 2000 characters';
    }

    // Validate type
    const allowedTypes = ['video', 'file', 'youtube', 'article', 'assessment'];
    if (!form.type || !allowedTypes.includes(form.type)) {
      errors.type = 'Please select a valid lesson type';
    }

    // Validate duration - required for video and youtube lessons
    const requiresDuration = form.type === 'video' || form.type === 'youtube';
    if (requiresDuration) {
      const hasAutoDuration = form.videoFile && extractedVideoDuration !== null;
      if ((!form.duration || form.duration === '') && !hasAutoDuration) {
        errors.duration = 'Duration is required';
      } else if (form.duration && (isNaN(Number(form.duration)) || Number(form.duration) <= 0)) {
        errors.duration = 'Duration must be greater than 0';
      } else if (form.duration && Number(form.duration) > 1000) {
        errors.duration = 'Duration must be less than 1000 minutes';
      }
    }

    // For video lessons, validate that entered duration matches extracted video duration
    if (form.type === 'video' && form.videoFile && extractedVideoDuration !== null) {
      const enteredDuration = Number(form.duration);
      if (!isNaN(enteredDuration) && enteredDuration > 0) {
        // Allow ±1 minute tolerance for rounding differences
        const tolerance = 1;
        const minAllowed = extractedVideoDuration - tolerance;
        const maxAllowed = extractedVideoDuration + tolerance;

        if (enteredDuration < minAllowed || enteredDuration > maxAllowed) {
          errors.duration = `Duration must match the video file duration (${formatDuration(extractedVideoDuration)}). Please enter the correct duration.`;
        }
      }
    }

    // Validate order
    if (!form.order || form.order === '') {
      errors.order = 'Order is required';
    } else if (isNaN(Number(form.order)) || Number(form.order) < 1) {
      errors.order = 'Order must be at least 1';
    } else if (!Number.isInteger(Number(form.order))) {
      errors.order = 'Order must be a whole number';
    } else if (Number(form.order) > 1000) {
      errors.order = 'Order must be less than 1000';
    }

    // Validate pass score if graded
    if (form.isGraded) {
      if (isNaN(Number(form.passScore)) || Number(form.passScore) < 0 || Number(form.passScore) > 100) {
        errors.passScore = 'Pass score must be between 0 and 100';
      }
    }

    // Validate file uploads based on lesson type
    if (form.type === 'video') {
      if (!form.videoFile) {
        errors.videoFile = 'Video file is required for video lessons';
      } else if (form.videoFile.file && form.videoFile.file.size > 500 * 1024 * 1024) { // 500MB
        errors.videoFile = 'Video file must be less than 500MB';
      }
    } else if (form.type === 'file') {
      if (!form.docFile) {
        errors.docFile = 'Document file is required for document lessons';
      }
    } else if (form.type === 'youtube') {
      if (!form.youtubeUrl || form.youtubeUrl.trim() === '') {
        errors.youtubeUrl = 'YouTube URL is required';
      } else if (!form.youtubeUrl.includes('youtube.com') && !form.youtubeUrl.includes('youtu.be')) {
        errors.youtubeUrl = 'Please enter a valid YouTube URL';
      }
    }

    return errors;
  };

  // Load available courses when no courseId is provided
  const loadAvailableCourses = async () => {
    try {
      setCoursesLoading(true);
      setError(null);

      // Use the same API call as training catalog for consistency
      const result = await trainingService.getCourses({
        search: '',
        type: 'all',
        difficulty: 'all',
        duration: 'all',
        compliance: 'all',
        status: 'all',
        page: 1,
        limit: 100, // Get more courses for selection
        sortBy: 'title',
        sortOrder: 'asc'
      });

      if (result.success) {
        const courses = result.data?.courses || [];
        setAvailableCourses(Array.isArray(courses) ? courses : []);
      } else {
        setError(result.error || 'Failed to load courses');
        setAvailableCourses([]);
      }
    } catch (error) {
      setError('Failed to load courses. Please try again.');
      setAvailableCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  };

  // Load modules from API with comprehensive error handling
  const loadModules = useCallback(async () => {

    // If no courseId, load available courses instead
    if (!courseId) {
      await loadAvailableCourses();
      return;
    }

    if (typeof courseId !== 'string' || courseId.trim() === '') {
      setError('Invalid course ID format');
      return;
    }

    // Check if courseId looks like a valid ID (basic validation)
    if (courseId.length < 3 || courseId.length > 50) {
      setError('Course ID must be between 3 and 50 characters');
      return;
    }

    setLoading(true);
    setError(null);
    setValidationErrors({});

    // Clear any cached lesson data to ensure fresh API data
    setModuleLessons({});

    try {
      // Use the new course structure API
      const result = await trainingService.getCourseStructure(courseId);

      if (!result || typeof result !== 'object') {
        throw new Error('Invalid response format from course structure API');
      }

      if (result.success) {
        const courseStructure = result.data || {};
        const modules = courseStructure.modules || [];

        // Validate that modules is an array
        if (Array.isArray(modules)) {
          // Debug each module's lessons
          modules.forEach((module, index) => {

            // Note: Backend doesn't include lessons in course structure response
            // Lessons will be fetched separately when modules are expanded
          });

          setModules(modules);

          // Set course info from course structure
          if (courseStructure.course) {
            setCourse({
              id: courseStructure.course.id,
              title: courseStructure.course.title,
              code: courseStructure.course.courseId,
              description: courseStructure.course.description,
              category: courseStructure.course.category,
              level: courseStructure.course.level,
              duration: courseStructure.course.duration,
              instructor: courseStructure.course.instructor,
              status: courseStructure.course.status,
              isActive: courseStructure.course.isActive
            });
          }
        } else {
          setModules([]);
        }
      } else {
        const errorMessage = result.error || 'Failed to load modules';
        setError(errorMessage);
        setModules([]);
        setCourse(null);
      }
    } catch (error) {
      setError('Failed to load modules. Please try again.');
      setModules([]);
      setCourse(null);
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
    }
  }, [courseId]);

  // Function to fetch lessons for a specific module
  const loadModuleLessons = async (moduleId, forceReload = false) => {
    if (!courseId || !moduleId) {
      return;
    }

    // Check if we already have lessons cached for this module (unless force reload)
    if (!forceReload && moduleLessons[moduleId] && moduleLessons[moduleId].length > 0) {
      return;
    }

    setLoadingLessons(prev => ({ ...prev, [moduleId]: true }));

    try {
      // Since the backend doesn't properly support lessons in the schema,
      // we'll use the course structure endpoint and check if lessons exist
      const result = await trainingService.getCourseStructure(courseId);

      if (result.success) {
        const courseStructure = result.data;
        const modules = courseStructure.modules || [];

        // Find the specific module and get its lessons
        const targetModule = modules.find(module => module.id === moduleId);
        const lessons = targetModule?.lessons || [];

        // Ensure lessons have proper lessonId field for updates
        const formattedLessons = lessons.map(lesson => ({
          ...lesson,
          lessonId: lesson.lessonId || lesson.id, // Ensure lessonId is set
          id: lesson.id || lesson.lessonId // Ensure id is set
        }));

        // If no lessons found, show empty state
        if (lessons.length === 0) {
        }

        setModuleLessons(prev => ({ ...prev, [moduleId]: formattedLessons }));
      } else {
        setError(result.error || 'Failed to load lessons');
        setModuleLessons(prev => ({ ...prev, [moduleId]: [] }));
      }
    } catch (error) {
      setError('Failed to load lessons. Please try again.');
      setModuleLessons(prev => ({ ...prev, [moduleId]: [] }));
    } finally {
      setLoadingLessons(prev => ({ ...prev, [moduleId]: false }));
    }
  };

  // Load modules on component mount
  useEffect(() => {
    // Reset initialization ref for new courseId
    initializationRef.current = false;

    // Always call loadModules for new courseId - force refresh
    // Reset states to force fresh load
    setInitialLoadComplete(false);
    setError(null);

    try {
      loadModules();
    } catch (error) {
    }

    // Cleanup function to prevent memory leaks
    return () => {
      initializationRef.current = false;
    };
  }, []); // Run only once on mount, not on courseId change

  // Always load available courses for course selection
  useEffect(() => {
    loadAvailableCourses();
  }, []);


  // Immediate useEffect to force API call
  useEffect(() => {
    // Force reset states
    setInitialLoadComplete(false);
    setError(null);

    // Call loadModules directly
    if (courseId) {
      // Call the API directly without depending on loadModules function
      const callAPI = async () => {
        try {
          const result = await trainingService.getCourseStructure(courseId);

          if (result.success) {
            const courseStructure = result.data || {};
            const modules = courseStructure.modules || [];
            setModules(modules);
            setInitialLoadComplete(true);
            setError(null);
          } else {
            setError(result.error || 'Failed to load course structure');
          }
        } catch (error) {
          setError('Failed to load course structure');
        }
      };

      callAPI();
    } else {
      loadAvailableCourses();
    }
  }, []);

  // Mock data removed - using API only


  // Handlers
  const handleBack = () => {
    navigate(-1);
  };

  const handleAddModule = () => {
    resetModuleForm();
    setModuleForm(prev => ({
      ...prev,
      order: modules.length + 1
    }));
    setShowAddModule(true);
  };

  const handleAddLesson = (moduleId) => {

    // Store the module ID in ref so it persists across re-renders
    currentModuleIdRef.current = moduleId;
    resetLessonForm();

    // Calculate the next order number based on existing lessons in this module
    const module = modules.find(m => m.id === moduleId);
    const existingLessons = moduleLessons[moduleId] || [];
    const maxOrder = existingLessons.length > 0
      ? Math.max(...existingLessons.map(lesson => lesson.order || 0))
      : 0;
    const nextOrder = maxOrder + 1;

    setLessonForm(prev => ({
      ...prev,
      order: nextOrder // Set order to next available number
    }));
    setShowAddLesson(true);
  };

  const handleEditModule = (module) => {
    resetModuleForm();
    setEditingModule(module);
    // Note: Modules are organizational containers only - removed content, duration, files, prerequisites, learningObjectives
    setModuleForm({
      title: module.title,
      description: module.description,
      order: module.order,
      isLocked: module.isLocked
    });
    setShowAddModule(true);
  };

  const handleEditLesson = (lesson, moduleId) => {
    resetLessonForm();
    setEditingLesson({ ...lesson, moduleId });

    // Process files - show all files, even without URLs
    const processedFiles = Array.isArray(lesson.files) ? lesson.files
      .filter(file => file && file.name && file.name.trim() !== '')
      .map((file, index) => ({
        id: file.id || `existing-${index}`,
        name: file.name,
        type: file.type || 'file',
        size: file.size || 0,
        url: file.url || '',
        publicId: file.publicId || '',
        format: file.format || '',
        width: file.width || 0,
        height: file.height || 0,
        uploadedAt: file.uploadedAt || new Date()
      })) : [];

    let videoFile = null;
    let docFile = null;
    let youtubeUrl = '';
    let detectedType = lesson.type || 'video';

    processedFiles.forEach(file => {
      const extension = file.name ? file.name.toLowerCase().split('.').pop() : '';
      const isVideo = file.type === 'video' || ['mp4', 'webm', 'ogg'].includes(extension);
      const isDoc = file.type === 'file' || ['pdf', 'doc', 'docx', 'ppt', 'pptx'].includes(extension);
      const isYoutube = file.type === 'youtube';

      if (isVideo && !videoFile) {
        videoFile = file;
        detectedType = 'video';
      } else if (isDoc && !docFile) {
        docFile = file;
        detectedType = 'file';
      } else if (isYoutube) {
        youtubeUrl = file.url;
        detectedType = 'youtube';
      }
    });

    setLessonForm({
      title: lesson.title,
      description: lesson.description,
      type: detectedType,
      content: lesson.content,
      duration: lesson.duration || '',
      isGraded: lesson.isGraded,
      passScore: lesson.passScore,
      order: lesson.order || '',
      prerequisites: lesson.prerequisites,
      isLocked: lesson.isLocked,
      videoFile: videoFile,
      docFile: docFile,
      youtubeUrl: youtubeUrl,
      externalLinks: lesson.externalLinks || []
    });

    // Clear extracted duration when editing (will be re-extracted if new video is uploaded)
    setExtractedVideoDuration(null);


    setShowAddLesson(true);
  };

  const handleSaveModule = async () => {

    // Validate form before submission
    const formErrors = validateModuleForm(moduleForm);

    if (Object.keys(formErrors).length > 0) {
      setValidationErrors(formErrors);
      setError(null); // Clear general error, show field-specific errors

      // Auto-scroll to first validation error
      setTimeout(() => {
        const firstErrorField = Object.keys(formErrors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorField}"], [data-field="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          errorElement.focus();
        } else {
          // Fallback: scroll to first error field by finding any field with validation error
          const errorFields = document.querySelectorAll('[data-field]');
          for (let field of errorFields) {
            const fieldName = field.getAttribute('data-field');
            if (formErrors[fieldName]) {
              field.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
              field.focus();
              break;
            }
          }
        }
      }, 100);

      return;
    }

    setLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      if (editingModule) {
        // Update existing module using course structure API
        if (!editingModule.id) {
          setError('No module ID found for update');
          return;
        }

        // Simplified module data - prerequisites and learningObjectives removed
        // If order is empty, keep the existing order; otherwise use the provided value
        const orderValue = moduleForm.order === '' ? editingModule.order : (Number(moduleForm.order) || 0);
        const moduleData = {
          title: moduleForm.title,
          description: moduleForm.description,
          order: orderValue,
          isLocked: moduleForm.isLocked
        };
        const result = await trainingService.updateModuleInCourse(courseId, editingModule.id, moduleData);
        if (result.success) {
          setShowAddModule(false);
          resetModuleForm();
          loadModules(); // Reload modules
          toast.success('Module updated successfully!');
        } else {
          setError(result.error || 'Failed to update module');
        }
      } else {
        // Create new module using course structure API
        if (!courseId) {
          setError('No course ID found for module creation');
          return;
        }

        // Simplified module data - prerequisites and learningObjectives removed
        // If order is empty, default to next available order number
        const orderValue = moduleForm.order === '' ? (modules.length + 1) : (Number(moduleForm.order) || modules.length + 1);
        const moduleData = {
          title: moduleForm.title,
          description: moduleForm.description,
          order: orderValue,
          isLocked: moduleForm.isLocked
        };
        const result = await trainingService.addModuleToCourse(courseId, moduleData);

        if (result.success) {
          setShowAddModule(false);
          resetModuleForm();
          loadModules(); // Reload modules
          toast.success('Module created successfully!');
        } else {
          setError(result.error || 'Failed to create module');
        }
      }
    } catch (error) {
      setError('Failed to save module. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLesson = async () => {

    // Convert string values to numbers for validation and submission
    const formData = {
      ...lessonForm,
      duration: lessonForm.duration === '' ? 0 : Number(lessonForm.duration),
      order: lessonForm.order === '' ? 0 : Number(lessonForm.order),
      passScore: lessonForm.passScore === '' ? 80 : Number(lessonForm.passScore),
      // Include uploaded file information for backend processing
      // Consolidate all file types into a single files array (consistent with course creation)
      files: [
        ...(lessonForm.videoFile ? [{
          name: lessonForm.videoFile.name || '',
          type: 'video',
          size: lessonForm.videoFile.size || 0,
          url: lessonForm.videoFile.url || '',
          publicId: lessonForm.videoFile.publicId || '',
          format: lessonForm.videoFile.format || '',
          uploadedAt: lessonForm.videoFile.uploadedAt
            ? (lessonForm.videoFile.uploadedAt instanceof Date
              ? lessonForm.videoFile.uploadedAt.toISOString()
              : lessonForm.videoFile.uploadedAt)
            : new Date().toISOString()
        }] : []),
        ...(lessonForm.docFile ? [{
          name: lessonForm.docFile.name || '',
          type: 'file',
          size: lessonForm.docFile.size || 0,
          url: lessonForm.docFile.url || '',
          publicId: lessonForm.docFile.publicId || '',
          format: lessonForm.docFile.format || '',
          uploadedAt: lessonForm.docFile.uploadedAt
            ? (lessonForm.docFile.uploadedAt instanceof Date
              ? lessonForm.docFile.uploadedAt.toISOString()
              : lessonForm.docFile.uploadedAt)
            : new Date().toISOString()
        }] : []),
        ...(lessonForm.youtubeUrl ? [{
          name: 'YouTube Content',
          type: 'youtube',
          url: lessonForm.youtubeUrl,
          uploadedAt: new Date().toISOString()
        }] : [])
      ]
    };
    if (formData.type === 'video' && (!formData.duration || formData.duration === 0) && extractedVideoDuration !== null) {
      formData.duration = extractedVideoDuration;
    }

    // Validate form before submission
    const formErrors = validateLessonForm(formData);
    if (Object.keys(formErrors).length > 0) {
      setValidationErrors(formErrors);
      setError(null); // Clear general error, show field-specific errors

      // Show helpful toast with specific error fields
      const errorFields = Object.keys(formErrors).map(field => {
        // Map internal names to user-friendly names
        const fieldMap = {
          title: 'Title',
          description: 'Description',
          content: 'Content',
          duration: 'Duration',
          order: 'Order',
          videoFile: 'Video File',
          docFile: 'Document File',
          youtubeUrl: 'YouTube URL'
        };
        return fieldMap[field] || field;
      });
      toast.error(`Please fix the errors in: ${errorFields.join(', ')}`);

      // Auto-scroll to first validation error
      setTimeout(() => {
        const firstErrorField = Object.keys(formErrors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorField}"], [data-field="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          errorElement.focus();
        } else {
          // Fallback: scroll to first error field by finding any field with validation error
          const errorFields = document.querySelectorAll('[data-field]');
          for (let field of errorFields) {
            const fieldName = field.getAttribute('data-field');
            if (formErrors[fieldName]) {
              field.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
              field.focus();
              break;
            }
          }
        }
      }, 100);

      return;
    }

    setLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      if (editingLesson) {
        // Update existing lesson using course structure API
        if (!editingLesson.id || !editingLesson.moduleId) {
          setError('No lesson ID or module ID found for update');
          return;
        }

        // Use MongoDB _id (editingLesson.id) for updates, not lessonId string
        // The backend can handle both, but _id is more reliable
        const lessonIdToUse = editingLesson.id || editingLesson.lessonId;

        const result = await trainingService.updateLessonInModule(courseId, editingLesson.moduleId, lessonIdToUse, formData);
        if (result.success) {

          setShowAddLesson(false);
          resetLessonForm();

          // Clear lesson cache to ensure fresh data
          setModuleLessons(prev => ({ ...prev, [editingLesson.moduleId]: [] }));

          // Reload modules to update lesson counts
          await loadModules();

          // Reload the specific module's lessons from the API (force reload)
          await loadModuleLessons(editingLesson.moduleId, true);
          toast.success('Lesson updated successfully!');
        } else {
          setError(result.error || 'Failed to update lesson');
        }
      } else {
        // Create new lesson using course structure API

        // Use the module ID directly from ref
        const moduleId = currentModuleIdRef.current;
        if (!moduleId) {
          setError('No module ID available for lesson creation');
          return;
        }


        const result = await trainingService.addLessonToModule(courseId, moduleId, formData);

        if (result.success) {

          setShowAddLesson(false);
          resetLessonForm();

          // Clear lesson cache to ensure fresh data
          setModuleLessons(prev => ({ ...prev, [moduleId]: [] }));

          // Reload modules to update lesson counts (like course creation does)
          await loadModules();

          // Also reload the specific module's lessons to show the new lesson (force reload)
          await loadModuleLessons(moduleId, true);
          toast.success('Lesson created successfully!');
        } else {
          setError(result.error || 'Failed to create lesson');
        }
      }
    } catch (error) {
      setError('Failed to save lesson. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModule = (moduleId) => {
    setModuleToDelete(moduleId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteModule = async () => {
    if (!moduleToDelete) return;

    setLoading(true);
    try {
      const result = await trainingService.deleteModuleFromCourse(courseId, moduleToDelete);
      if (result.success) {
        loadModules(); // Reload modules
        toast.success('Module deleted successfully!');
      } else {
        setError(result.error || 'Failed to delete module');
      }
    } catch (error) {
      setError('Failed to delete module');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setModuleToDelete(null);
    }
  };

  const cancelDeleteModule = () => {
    setShowDeleteConfirm(false);
    setModuleToDelete(null);
  };

  const handleDeleteLesson = async (lesson, moduleId) => {
    const isConfirmed = await window.confirm('Are you sure you want to delete this lesson?');
    if (isConfirmed) {
      setLoading(true);
      try {
        const lessonId = lesson.id;

        // Delete any physical files stored on Cloudinary logic
        if (lesson.files && Array.isArray(lesson.files)) {
          for (const file of lesson.files) {
            if (file.publicId && (file.type === 'video' || file.type === 'file' || file.type === 'doc')) {
              await trainingService.deleteTrainingFile(file.publicId).catch(err => {
              });
            }
          }
        }

        const result = await trainingService.deleteLessonFromModule(courseId, moduleId, lessonId);
        if (result.success) {
          // Reload modules to update lesson counts
          await loadModules();

          // Reload the specific module's lessons from the API
          await loadModuleLessons(moduleId);
          toast.success('Lesson deleted successfully!');
        } else {
          setError(result.error || 'Failed to delete lesson');
        }
      } catch (error) {
        setError('Failed to delete lesson');
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle file deletion from Cloudinary
  const handleDeleteFile = async (fileData, fileType) => {
    try {
      if (!fileData?.publicId) {
        return false;
      }

      // Set loading state
      setDeletingFiles(prev => ({ ...prev, [fileType]: true }));


      const result = await trainingService.deleteTrainingFile(fileData.publicId);

      if (result.success) {
        toast.success(`${fileType === 'video' ? 'Video' : 'Document'} deleted successfully`);
        return true;
      } else {
        // Show a subtle notification to user
        setError(`Failed to delete ${fileType} file from cloud storage. File removed from form but may still exist in cloud.`);
        return false;
      }
    } catch (error) {
      // Show a subtle notification to user
      setError(`Error deleting ${fileType} file from cloud storage. File removed from form but may still exist in cloud.`);
      return false;
    } finally {
      // Clear loading state
      setDeletingFiles(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const initiateFileDelete = (fileData, fileType, onSuccess) => {
    if (!fileData) {
      onSuccess?.();
      return;
    }
    setFileDeleteConfirm({
      show: true,
      fileType,
      fileData,
      onSuccess
    });
  };

  const closeFileDeleteConfirm = () => {
    setFileDeleteConfirm(initialFileDeleteState);
  };

  const confirmFileDelete = async () => {
    if (!fileDeleteConfirm.fileData || !fileDeleteConfirm.fileType) {
      closeFileDeleteConfirm();
      return;
    }
    const deleted = await handleDeleteFile(fileDeleteConfirm.fileData, fileDeleteConfirm.fileType);
    if (deleted) {
      fileDeleteConfirm.onSuccess?.();
    }
    closeFileDeleteConfirm();
  };

  const ensureFilenameHasExtension = (filename, mimeType) => {
    const clean = String(filename || 'file').trim();
    if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

    const type = String(mimeType || '').toLowerCase();
    if (type === 'video/mp4') return `${clean}.mp4`;
    if (type === 'video/webm') return `${clean}.webm`;
    if (type === 'video/ogg') return `${clean}.ogg`;
    if (type === 'application/pdf') return `${clean}.pdf`;
    if (type === 'application/msword') return `${clean}.doc`;
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return `${clean}.docx`;
    if (type === 'application/vnd.ms-powerpoint') return `${clean}.ppt`;
    if (type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return `${clean}.pptx`;
    return clean;
  };

  const getFilenameFromUrl = (url) => {
    try {
      const parsed = new URL(url);
      const lastPart = decodeURIComponent(parsed.pathname.split('/').pop() || '').trim();
      return /\.[a-zA-Z0-9]+$/.test(lastPart) ? lastPart : '';
    } catch (error) {
      return '';
    }
  };

  const handleViewUploadedFile = async (fileUrl, fileName = 'file') => {
    if (!fileUrl) {
      toast.error('No file URL available');
      return;
    }

    try {
      const response = await authFetch(fileUrl);
      if (!response.ok) throw new Error('Failed to fetch file');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const finalName = ensureFilenameHasExtension(
        fileName || getFilenameFromUrl(fileUrl) || 'file',
        blob.type
      );

      // Open blob URL for preview and also set a meaningful download name fallback.
      const previewWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!previewWindow) {
        toast.error('Unable to open file preview. Please allow popups for this site.');
      }

      // Keep URL alive briefly for browser preview, then cleanup.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 15000);
      // Store inferred name for consistent follow-up download action.
      return finalName;
    } catch (error) {
      // Fallback for CORS-restricted URLs.
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownloadUploadedFile = async (fileUrl, fileName = 'file') => {
    if (!fileUrl) {
      toast.error('No file URL available');
      return;
    }

    const baseName = fileName || getFilenameFromUrl(fileUrl) || 'file';

    try {
      const response = await authFetch(fileUrl);
      if (!response.ok) throw new Error('Failed to fetch file');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const finalName = ensureFilenameHasExtension(baseName, blob.type);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = finalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      // Fallback for CORS-restricted URLs.
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = ensureFilenameHasExtension(baseName, '');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const formatFileSize = (bytes) => {
    const size = Number(bytes);
    if (!size || size <= 0) return 'Unknown size';
    const mb = size / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    const kb = size / 1024;
    return `${kb.toFixed(2)} KB`;
  };

  const openLessonDetailsModal = (lesson, module) => {
    setLessonDetailsModal({
      show: true,
      lesson,
      module
    });
  };

  const closeLessonDetailsModal = () => {
    setLessonDetailsModal({
      show: false,
      lesson: null,
      module: null
    });
  };

  const handleVideoFileSelect = async (file) => {
    if (!file) return;

    // Validate file
    const validation = validateFile(file, ['mp4', 'webm', 'ogg'], 500);
    if (!validation.isValid) {
      setError(`File validation failed: ${validation.errors.join(', ')}`);
      return;
    }

    setUploadingFiles(prev => ({ ...prev, video: true }));
    setUploadProgress(prev => ({ ...prev, video: 0 }));
    setError(null);

    // Extract video duration for validation
    let videoDuration = null;
    try {
      videoDuration = await new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
          window.URL.revokeObjectURL(video.src);
          // Convert seconds to minutes, round to 2 decimal places
          const durationInMinutes = Math.round((video.duration / 60) * 100) / 100;
          // Format to ensure proper decimal display (max 2 decimals)
          const formattedDuration = parseFloat(durationInMinutes.toFixed(2));
          resolve(formattedDuration);
        };

        video.onerror = () => {
          window.URL.revokeObjectURL(video.src);
          reject(new Error('Failed to load video metadata'));
        };

        video.src = URL.createObjectURL(file);
      });
      setExtractedVideoDuration(videoDuration);
    } catch (error) {
      setExtractedVideoDuration(null);
    }

    try {
      setUploadProgress(prev => ({ ...prev, video: 25 }));
      const result = await trainingService.uploadTrainingFile(file, 'video');
      if (result.success) {
        setUploadProgress(prev => ({ ...prev, video: 100 }));
        setLessonForm(prev => ({
          ...prev,
          videoFile: {
            file: file,
            url: result.data.url,
            name: result.data.name || file.name,
            type: 'video',
            size: result.data.size,
            publicId: result.data.publicId,
            format: result.data.format,
            uploadedAt: new Date()
          },
          duration: videoDuration || prev.duration
        }));
        setValidationErrors(prev => ({ ...prev, videoFile: null }));
        setTimeout(() => setUploadProgress(prev => ({ ...prev, video: 0 })), 1000);
      } else {
        setError(result.error || 'Failed to upload video file');
      }
    } catch (error) {
      setError('Failed to upload video file. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, video: false }));
    }
  };

  const handleDocumentFileSelect = async (file) => {
    if (!file) return;

    const validation = validateFile(file, ['pdf', 'doc', 'docx', 'ppt', 'pptx'], 100);
    if (!validation.isValid) {
      setError(`File validation failed: ${validation.errors.join(', ')}`);
      return;
    }

    setUploadingFiles(prev => ({ ...prev, doc: true }));
    setUploadProgress(prev => ({ ...prev, doc: 0 }));
    setError(null);

    try {
      setUploadProgress(prev => ({ ...prev, doc: 30 }));
      const result = await trainingService.uploadTrainingFile(file, 'doc');
      if (result.success) {
        setUploadProgress(prev => ({ ...prev, doc: 100 }));
        setLessonForm(prev => ({
          ...prev,
          docFile: {
            url: result.data.url,
            name: result.data.name || file.name,
            type: 'file',
            size: result.data.size,
            publicId: result.data.publicId,
            format: result.data.format,
            uploadedAt: new Date()
          }
        }));
        setValidationErrors(prev => ({ ...prev, docFile: null }));
        setTimeout(() => setUploadProgress(prev => ({ ...prev, doc: 0 })), 1000);
      } else {
        setError(result.error || 'Failed to upload document');
      }
    } catch (error) {
      setError('Failed to upload document. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, doc: false }));
    }
  };

  const handleDragOver = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(prev => ({ ...prev, [type]: true }));
  };

  const handleDragLeave = (e, type) => {
    e.preventDefault();
    e.stopPropagation();

    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragActive(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDrop = async (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(prev => ({ ...prev, [type]: false }));

    const file = e.dataTransfer?.files?.[0];
    if (!file) return;

    if (type === 'video') {
      await handleVideoFileSelect(file);
    } else if (type === 'doc') {
      await handleDocumentFileSelect(file);
    }
  };

  const handleToggleModule = (moduleId) => {
    const isExpanding = !expandedModules[moduleId];

    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));

    // If expanding the module and we don't have lessons loaded yet, fetch them
    if (isExpanding && (!moduleLessons[moduleId] || moduleLessons[moduleId].length === 0)) {
      loadModuleLessons(moduleId);
    }
  };


  const getLessonTypeIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'youtube': return <Video className="h-4 w-4 text-red-600" />;
      case 'article': return <FileText className="h-4 w-4" />;
      case 'file': return <BookOpen className="h-4 w-4" />;
      case 'assessment': return <CheckCircle className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getLessonTypeColor = (type) => {
    switch (type) {
      case 'video': return 'text-blue-600 bg-blue-100';
      case 'youtube': return 'text-red-600 bg-red-100';
      case 'article': return 'text-green-600 bg-green-100';
      case 'file': return 'text-purple-600 bg-purple-100';
      case 'assessment': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) {
      return '0s';
    }
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    // Show seconds if there are any, or if there are no hours/minutes
    if (secs > 0) parts.push(`${secs}s`);

    return parts.length > 0 ? parts.join(' ') : '0s';
  };

  const getDisplayDurationValue = () => {
    if (lessonForm.duration !== '' && lessonForm.duration !== null && lessonForm.duration !== undefined) {
      const parsed = Number(lessonForm.duration);
      return isNaN(parsed) ? 0 : parsed;
    }
    if (extractedVideoDuration !== null) {
      return extractedVideoDuration;
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {error.includes('Course ID is missing') ? 'Course Not Available' :
                error.includes('Invalid course ID') ? 'Course Not Found' :
                  error.includes('Course ID must be') ? 'Invalid Course ID' :
                    'Error Loading Course'}
            </h2>
            <p className="text-gray-600 mb-6">
              {error.includes('Course ID is missing')
                ? 'The requested course is not available at this time. Please select a course from the training catalog to view its structure and content.'
                : error.includes('Invalid course ID')
                  ? 'The course you\'re looking for doesn\'t exist or has been removed.'
                  : error.includes('Course ID must be')
                    ? 'The course ID format is invalid. Please check the URL and try again.'
                    : error
              }
            </p>
          </div>

          <div className="space-y-3">
            <PermissionGate permission="training" action="read">
              <button
                onClick={handleBack}
                className="w-full flex items-center justify-center px-4 py-3 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                {error.includes('Course ID is missing') ? 'Browse Training Catalog' : 'Back to Training Catalog'}
              </button>
            </PermissionGate>

            {!error.includes('Course ID is missing') && !error.includes('Invalid course ID') && !error.includes('Course ID must be') && (
              <PermissionGate permission="training" action="read">
                <button
                  onClick={loadModules}
                  className="w-full flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <RefreshCw className="h-5 w-5 mr-2" />
                  Try Again
                </button>
              </PermissionGate>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              {error.includes('Course ID is missing')
                ? 'Browse our training catalog to discover available courses and start your learning journey.'
                : 'Need assistance? Contact your training administrator or explore the training catalog for available courses.'
              }
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show course selection when no courseId is provided
  if (!courseId) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <PermissionGate permission="training" action="read">
              <button
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
              </button>
            </PermissionGate>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Course Structure Management</h1>
              <p className="text-gray-600 mt-1">Select a course to manage its structure and content</p>
            </div>
          </div>
        </div>

        {/* Course Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Courses</h2>

          {coursesLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-agrosaf-green" />
              <span className="ml-2 text-gray-600">Loading courses...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">{error}</p>
              <PermissionGate permission="training" action="read">
                <button
                  onClick={loadAvailableCourses}
                  className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
                >
                  Try Again
                </button>
              </PermissionGate>
            </div>
          ) : !Array.isArray(availableCourses) || availableCourses.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No courses available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(Array.isArray(availableCourses) ? availableCourses : []).map((course) => (
                <div
                  key={course.id}
                  onClick={() => navigate(`/modules_lessons/${course.id}`)}
                  className="p-4 border border-gray-200 rounded-lg hover:border-agrosaf-green hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-gray-900 group-hover:text-agrosaf-green transition-colors">
                      {course.title}
                    </h3>
                    <Settings className="h-4 w-4 text-gray-400 group-hover:text-agrosaf-green transition-colors" />
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{course.code}</p>
                  <p className="text-xs text-gray-500 line-clamp-2">{course.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-gray-500">
                      {course.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                    <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-agrosaf-green transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Check if data is loaded - prevent showing default values
  if (loading || !initialLoadComplete) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <RefreshCw className="h-8 w-8 animate-spin text-agrosaf-green" />
        <span className="ml-2 text-gray-600">Loading course structure...</span>
        <PermissionGate permission="training" action="read">
          <button
            onClick={() => {
              setInitialLoadComplete(false);
              setError(null);
              loadModules();
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Force Load Course
          </button>
        </PermissionGate>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <PermissionGate permission="training" action="read">
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
            </button>
          </PermissionGate>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Course Structure Management</h1>
            <p className="text-gray-600 mt-1 truncate max-w-2xl">
              {courseId ? `Managing: ${course?.title}` : 'Select a course to manage its structure'}
            </p>
          </div>
        </div>

        <div className="flex space-x-2">
          <PermissionGate permission="training" action="create">
            <button
              onClick={handleAddModule}
              className="flex items-center px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Module
            </button>
          </PermissionGate>
        </div>
      </div>


      {/* Course Stats */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{modules.length}</div>
            <div className="text-sm text-gray-600">Modules</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {Object.values(moduleLessons).reduce((total, lessons) => total + (lessons?.length || 0), 0) ||
                modules.reduce((total, module) => total + (module.lessons?.length || 0), 0)}
            </div>
            <div className="text-sm text-gray-600">Lessons</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {(() => {
                // Calculate total duration only from lessons that have duration (video, quiz, assignment)
                const totalDuration =
                  Object.values(moduleLessons).reduce((total, lessons) =>
                    total + (lessons || []).reduce((lessonTotal, lesson) => {
                      // Only count duration for lesson types that require it
                      if ((lesson.type === 'video' || lesson.type === 'youtube') && lesson.duration > 0) {
                        return lessonTotal + (Number(lesson.duration) || 0);
                      }
                      return lessonTotal;
                    }, 0), 0
                  ) ||
                  modules.reduce((total, module) => total + (Number(module.duration) || 0), 0) ||
                  modules.reduce((total, module) =>
                    total + (module.lessons || []).reduce((lessonTotal, lesson) => {
                      if ((lesson.type === 'video' || lesson.type === 'youtube') && lesson.duration > 0) {
                        return lessonTotal + (Number(lesson.duration) || 0);
                      }
                      return lessonTotal;
                    }, 0), 0
                  );
                return formatDuration(totalDuration);
              })()}
            </div>
            <div className="text-sm text-gray-600">Duration</div>
          </div>
        </div>
      </div>

      {/* Modules List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-agrosaf-green mx-auto mb-4" />
            <p className="text-gray-600">Loading course structure...</p>
          </div>
        ) : modules && modules.length > 0 ? (
          modules.map((module, moduleIndex) => (
            <div key={module.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Module Header */}
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="flex items-center flex-shrink-0">
                      {module.isLocked ? (
                        <Lock className="h-5 w-5 text-red-500" />
                      ) : (
                        <Unlock className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        Module {module.order}: {module.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2 break-words">{module.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <PermissionGate permission="training" action="read">
                      <button
                        onClick={() => handleToggleModule(module.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {expandedModules[module.id] ? (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                    </PermissionGate>
                    <PermissionGate permission="training" action="update">
                      <button
                        onClick={() => handleEditModule(module)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </PermissionGate>
                    <PermissionGate permission="training" action="delete">
                      <button
                        onClick={() => handleDeleteModule(module.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </PermissionGate>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span className="flex items-center">
                      <BookOpen className="h-4 w-4 mr-1" />
                      {module.lessons?.length || 0} lessons
                    </span>
                    {(() => {
                      // Calculate module duration from lessons that have duration (video, quiz, assignment)
                      const moduleDuration =
                        (moduleLessons[module.id] || []).reduce((total, lesson) => {
                          // Only count duration for lesson types that require it
                          if ((lesson.type === 'video' || lesson.type === 'youtube') && lesson.duration > 0) {
                            return total + (Number(lesson.duration) || 0);
                          }
                          return total;
                        }, 0) ||
                        (Number(module.duration) || 0) ||
                        (module.lessons || []).reduce((total, lesson) => {
                          if ((lesson.type === 'video' || lesson.type === 'youtube') && lesson.duration > 0) {
                            return total + (Number(lesson.duration) || 0);
                          }
                          return total;
                        }, 0);

                      // Only show duration if it's greater than 0
                      return moduleDuration > 0 ? (
                        <span className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {formatDuration(moduleDuration)}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <PermissionGate permission="training" action="create">
                    <button
                      onClick={() => handleAddLesson(module.id)}
                      className="flex items-center px-3 py-1 text-sm bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Lesson
                    </button>
                  </PermissionGate>
                </div>
              </div>

              {/* Lessons List */}
              {expandedModules[module.id] && (
                <div className="p-4">

                  {loadingLessons[module.id] ? (
                    <div className="text-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin text-agrosaf-green mx-auto mb-2" />
                      <p className="text-gray-600">Loading lessons...</p>
                    </div>
                  ) : moduleLessons[module.id] && moduleLessons[module.id].length > 0 ? (
                    <div className="space-y-3">
                      {[...(moduleLessons[module.id] || [])]
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((lesson) => (
                          <div
                            key={lesson.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center">
                                {lesson.isLocked ? (
                                  <Lock className="h-4 w-4 text-red-500" />
                                ) : (
                                  <Unlock className="h-4 w-4 text-green-500" />
                                )}
                              </div>
                              <div className={`p-2 rounded-lg ${getLessonTypeColor(lesson.type)}`}>
                                {getLessonTypeIcon(lesson.type)}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <h4 className="font-medium text-gray-900 break-all break-word capitalize">{lesson.title}</h4>
                                  {lesson.isGraded && (
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                      Graded
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 break-all break-word first-letter:capitalize">{lesson.description}</p>
                                <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                                  {/* Only show duration for lesson types that require it (video, quiz, assignment) */}
                                  {(lesson.type === 'video' || lesson.type === 'youtube') && lesson.duration > 0 && (
                                    <span><Clock className="h-3 w-3 inline mr-1" />{formatDuration(lesson.duration)}</span>
                                  )}
                                  <span>Order: {lesson.order}</span>
                                  {lesson.isGraded && (
                                    <span>Pass: {lesson.passScore}%</span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => openLessonDetailsModal(lesson, module)}
                                className="p-1 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                title="View Lesson Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <PermissionGate permission="training" action="update">
                                <button
                                  onClick={() => handleEditLesson(lesson, module.id)}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="training" action="delete">
                                <button
                                  onClick={() => handleDeleteLesson(lesson, module.id)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="mb-4">
                        <BookOpen className="h-8 w-8 text-gray-400 mx-auto" />
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">No Lessons Yet</h4>
                      <p className="text-sm text-gray-600 mb-4">This module doesn't have any lessons yet.</p>
                      <PermissionGate permission="training" action="create">
                        <button
                          onClick={() => handleAddLesson(module.id)}
                          className="inline-flex items-center px-3 py-2 text-sm bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Lesson
                        </button>
                      </PermissionGate>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="mb-4">
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Modules Found</h3>
            <p className="text-gray-600 mb-4">This course doesn't have any modules yet.</p>
            <PermissionGate permission="training" action="create">
              <button
                onClick={handleAddModule}
                className="inline-flex items-center px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Module
              </button>
            </PermissionGate>
          </div>
        )}
      </div>

      {/* Add/Edit Module Modal */}
      {showAddModule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingModule ? 'Edit Module' : 'Add New Module'}
              </h2>
            </div>


            <div className="p-6">
              {/* Display general error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Module Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        data-field="title"
                        value={moduleForm.title}
                        maxLength={100}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Prevent entering more than maxLength
                          if (value.length > 100) return;

                          setModuleForm(prev => ({ ...prev, title: value }));

                          // Real-time validation for module title
                          let error = null;
                          if (value && value.trim().length > 0) {
                            if (value.trim().length < 3) {
                              error = 'Module title must be at least 3 characters';
                            } else if (value.trim().length > 100) {
                              error = 'Module title must be less than 100 characters';
                            } else if (value.length === 100) {
                              error = 'Maximum 100 characters reached';
                            }
                          }

                          setValidationErrors(prev => ({ ...prev, title: error }));
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'
                          }`}
                        placeholder="Enter module title (e.g., Introduction to Safety Protocols)"
                      />
                      <div className="mt-1 flex items-center justify-between">
                        {validationErrors.title && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.title}
                          </p>
                        )}
                        <p className={`text-xs ml-auto ${moduleForm.title.length === 100 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                          {moduleForm.title.length}/100
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="description"
                        data-field="description"
                        value={moduleForm.description}
                        maxLength={500}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Prevent entering more than maxLength
                          if (value.length > 500) return;

                          setModuleForm(prev => ({ ...prev, description: value }));

                          // Real-time validation for module description
                          let error = null;
                          if (value && value.trim().length > 0) {
                            if (value.trim().length < 10) {
                              error = 'Module description must be at least 10 characters';
                            } else if (value.trim().length > 500) {
                              error = 'Module description must be less than 500 characters';
                            } else if (value.length === 500) {
                              error = 'Maximum 500 characters reached';
                            }
                          }

                          setValidationErrors(prev => ({ ...prev, description: error }));
                        }}
                        rows={3}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'
                          }`}
                        placeholder="Enter module description (minimum 10 characters)"
                      />
                      <div className="mt-1 flex items-center justify-between">
                        {validationErrors.description && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.description}
                          </p>
                        )}
                        <p className={`text-xs ml-auto ${moduleForm.description.length === 500 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                          {moduleForm.description.length}/500
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Module Settings */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Module Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Order
                      </label>
                      <input
                        type="number"
                        name="order"
                        data-field="order"
                        value={moduleForm.order}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          // Allow empty string, otherwise parse as integer
                          let value = inputValue === '' ? '' : (parseInt(inputValue) || '');

                          // Prevent entering values beyond max
                          if (value !== '' && value > 1000) {
                            value = 1000;
                          }

                          setModuleForm(prev => ({ ...prev, order: value }));

                          // Real-time validation for module order
                          let error = null;
                          if (value !== '' && value !== null && value !== undefined) {
                            if (value < 1) {
                              error = 'Order must be at least 1';
                            } else if (value > 1000) {
                              error = 'Order must be less than 1000';
                            }
                          }

                          setValidationErrors(prev => ({ ...prev, order: error }));
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.order ? 'border-red-500 bg-red-50' : 'border-gray-300'
                          }`}
                        min="1"
                        max="1000"
                        placeholder="1"
                      />
                      <div className="mt-1 flex items-center justify-between">
                        {validationErrors.order && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.order}
                          </p>
                        )}
                        {moduleForm.order === 1000 && !validationErrors.order && (
                          <p className="text-xs text-red-600 font-medium ml-auto">
                            Maximum order value (1000) reached
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">
              <PermissionGate permission="training" action="read">
                <button
                  onClick={() => {
                    resetModuleForm();
                    setShowAddModule(false);
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </PermissionGate>
              <PermissionGate permission="training" action={editingModule ? "update" : "create"}>
                <button
                  onClick={() => {
                    handleSaveModule();
                  }}
                  className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
                >
                  <Save className="h-4 w-4 mr-2 inline" />
                  {editingModule ? 'Update Module' : 'Create Module'}
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Lesson Modal */}
      {showAddLesson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingLesson ? 'Edit Lesson' : 'Add New Lesson'}
              </h2>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lesson Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      data-field="title"
                      value={lessonForm.title}
                      maxLength={100}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Prevent entering more than maxLength
                        if (value.length > 100) return;

                        setLessonForm(prev => ({ ...prev, title: value }));

                        // Real-time validation for lesson title
                        let error = null;
                        if (value && value.trim().length > 0) {
                          if (value.trim().length < 3) {
                            error = 'Lesson title must be at least 3 characters';
                          } else if (value.trim().length > 100) {
                            error = 'Lesson title must be less than 100 characters';
                          } else if (value.length === 100) {
                            error = 'Maximum 100 characters reached';
                          }
                        }

                        setValidationErrors(prev => ({ ...prev, title: error }));
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      placeholder="Enter lesson title (e.g., Safety Equipment Overview)"
                    />
                    <div className="mt-1 flex items-center justify-between">
                      {validationErrors.title && (
                        <p className="text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {validationErrors.title}
                        </p>
                      )}
                      <p className={`text-xs ml-auto ${lessonForm.title.length === 100 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                        {lessonForm.title.length}/100
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    data-field="description"
                    value={lessonForm.description}
                    maxLength={500}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Prevent entering more than maxLength
                      if (value.length > 500) return;

                      setLessonForm(prev => ({ ...prev, description: value }));

                      // Real-time validation for lesson description
                      let error = null;
                      if (value && value.trim().length > 0) {
                        if (value.trim().length < 3) {
                          error = 'Lesson description must be at least 3 characters';
                        } else if (value.trim().length > 500) {
                          error = 'Lesson description must be less than 500 characters';
                        } else if (value.length === 500) {
                          error = 'Maximum 500 characters reached';
                        }
                      }

                      setValidationErrors(prev => ({ ...prev, description: error }));
                    }}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Enter lesson description (minimum 10 characters)"
                  />
                  <div className="mt-1 flex items-center justify-between">
                    {validationErrors.description && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {validationErrors.description}
                      </p>
                    )}
                    <p className={`text-xs ml-auto ${lessonForm.description.length === 500 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                      {lessonForm.description.length}/500
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="content"
                    data-field="content"
                    value={lessonForm.content}
                    maxLength={2000}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Prevent entering more than maxLength
                      if (value.length > 2000) return;

                      setLessonForm(prev => ({ ...prev, content: value }));

                      // Real-time validation for lesson content
                      let error = null;
                      if (value && value.trim().length > 0) {
                        if (value.trim().length < 3) {
                          error = 'Lesson content must be at least 3 characters';
                        } else if (value.trim().length > 2000) {
                          error = 'Lesson content must be less than 2000 characters';
                        } else if (value.length === 2000) {
                          error = 'Maximum 2000 characters reached';
                        }
                      }

                      setValidationErrors(prev => ({ ...prev, content: error }));
                    }}
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.content ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Enter lesson content (minimum 10 characters)"
                  />
                  <div className="mt-1 flex items-center justify-between">
                    {validationErrors.content && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {validationErrors.content}
                      </p>
                    )}
                    <p className={`text-xs ml-auto ${lessonForm.content.length === 2000 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                      {lessonForm.content.length}/2000
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lesson Type
                  </label>
                  <select
                    value={lessonForm.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setLessonForm(prev => ({ ...prev, type: newType }));
                      setValidationErrors(prev => ({ ...prev, type: null }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent bg-white text-gray-700"
                  >
                    <option value="video">Video Lesson (Upload)</option>
                    <option value="file">Document Lesson (PPT, PDF, DOC)</option>
                    <option value="youtube">YouTube Video Lesson</option>
                  </select>
                </div>
                {/* File Upload Section - based on lesson type */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Content Upload</h4>

                  {lessonForm.type === 'video' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Video File <span className="text-red-500">*</span>
                      </label>
                      <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragActive.video ? 'border-agrosaf-green bg-green-50' : 'border-gray-300 hover:border-agrosaf-green'}`}
                        onDragOver={(e) => handleDragOver(e, 'video')}
                        onDragLeave={(e) => handleDragLeave(e, 'video')}
                        onDrop={(e) => handleDrop(e, 'video')}
                      >
                        <input
                          type="file"
                          accept="video/mp4,video/webm,video/ogg"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            await handleVideoFileSelect(file);
                          }}
                          className="hidden"
                          id="video-upload"
                        />
                        <label htmlFor="video-upload" className="cursor-pointer">
                          {uploadingFiles.video ? (
                            <RefreshCw className="h-8 w-8 text-agrosaf-green mx-auto mb-2 animate-spin" />
                          ) : (
                            <Video className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          )}
                          <p className="text-sm text-gray-600">
                            {uploadingFiles.video
                              ? `Uploading video... ${uploadProgress.video}%`
                              : lessonForm.videoFile
                                ? lessonForm.videoFile.name
                                : 'Click to upload or drag and drop a video file'
                            }
                          </p>
                          {uploadingFiles.video && (
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                              <div
                                className="bg-agrosaf-green h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress.video}%` }}
                              ></div>
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Supported formats: MP4, WebM, OGG (Max 500MB)
                          </p>
                        </label>
                      </div>
                      {lessonForm.videoFile && (
                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Uploaded Video File:</h5>
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <Video className="h-5 w-5 text-blue-500" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{lessonForm.videoFile.name}</p>
                                <p className="text-xs text-gray-500">
                                  VIDEO • {(lessonForm.videoFile.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => handleDownloadUploadedFile(lessonForm.videoFile.url, lessonForm.videoFile.name || 'video-file')}
                                className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                title="Download file"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                disabled={deletingFiles.video}
                                onClick={() => {
                                  initiateFileDelete(lessonForm.videoFile, 'video', () => {
                                    setLessonForm(prev => ({ ...prev, videoFile: null, duration: '' }));
                                    setExtractedVideoDuration(null);
                                  });
                                }}
                                className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Delete file"
                              >
                                {deletingFiles.video ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {validationErrors.videoFile && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {validationErrors.videoFile}
                        </p>
                      )}
                    </div>
                  )}

                  {lessonForm.type === 'file' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Document File (PPT, PDF, DOC) <span className="text-red-500">*</span>
                      </label>
                      <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragActive.doc ? 'border-agrosaf-green bg-green-50' : 'border-gray-300 hover:border-agrosaf-green'}`}
                        onDragOver={(e) => handleDragOver(e, 'doc')}
                        onDragLeave={(e) => handleDragLeave(e, 'doc')}
                        onDrop={(e) => handleDrop(e, 'doc')}
                      >
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.ppt,.pptx"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            await handleDocumentFileSelect(file);
                          }}
                          className="hidden"
                          id="doc-upload"
                        />
                        <label htmlFor="doc-upload" className="cursor-pointer">
                          {uploadingFiles.doc ? (
                            <RefreshCw className="h-8 w-8 text-agrosaf-green mx-auto mb-2 animate-spin" />
                          ) : (
                            <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          )}
                          <p className="text-sm text-gray-600">
                            {uploadingFiles.doc
                              ? `Uploading document... ${uploadProgress.doc}%`
                              : lessonForm.docFile
                                ? lessonForm.docFile.name
                                : 'Click to upload or drag and drop PPT, PDF, or Word file'
                            }
                          </p>
                          {uploadingFiles.doc && (
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                              <div
                                className="bg-agrosaf-green h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress.doc}%` }}
                              ></div>
                            </div>
                          )}
                        </label>
                      </div>
                      {lessonForm.docFile && (
                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Uploaded Document:</h5>
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <FileText className="h-5 w-5 text-blue-500" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{lessonForm.docFile.name}</p>
                                <p className="text-xs text-gray-500">
                                  {(lessonForm.docFile.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            <PermissionGate permission="training" action="update">
                              <div className="flex items-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => handleDownloadUploadedFile(lessonForm.docFile.url, lessonForm.docFile.name || 'document-file')}
                                  className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                  title="Download file"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  disabled={deletingFiles.doc}
                                  onClick={() => {
                                    initiateFileDelete(lessonForm.docFile, 'doc', () => {
                                      setLessonForm(prev => ({ ...prev, docFile: null }));
                                    });
                                  }}
                                  className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete file"
                                >
                                  {deletingFiles.doc ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                </button>
                              </div>
                            </PermissionGate>
                          </div>
                        </div>
                      )}
                      {validationErrors.docFile && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {validationErrors.docFile}
                        </p>
                      )}
                    </div>
                  )}

                  {lessonForm.type === 'youtube' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        YouTube Video URL <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Video className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={lessonForm.youtubeUrl}
                          onChange={(e) => {
                            setLessonForm(prev => ({ ...prev, youtubeUrl: e.target.value }));
                            setValidationErrors(prev => ({ ...prev, youtubeUrl: null }));
                          }}
                          className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.youtubeUrl ? 'border-red-500 bg-red-50' : 'border-gray-300'
                            }`}
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                      </div>
                      {validationErrors.youtubeUrl && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {validationErrors.youtubeUrl}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        Enter the full URL of the YouTube video you want to include in this lesson.
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Duration field - show for video and youtube lessons */}
                  {(lessonForm.type === 'video' || lessonForm.type === 'youtube') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Duration (minutes) <span className="text-red-500">*</span>
                      </label>
                      {lessonForm.type === 'video' ? (
                        <>
                          {(() => {
                            const displayDurationValue = getDisplayDurationValue();
                            const formattedDuration = formatDuration(displayDurationValue);
                            return (
                              <>
                                {displayDurationValue > 0 && (
                                  <p className="text-xs text-gray-500 mb-1">
                                    Video duration: {formattedDuration}
                                  </p>
                                )}
                                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed">
                                  <span className="text-gray-700">
                                    {displayDurationValue > 0 ? formattedDuration : 'Duration will be auto-calculated from video file'}
                                  </span>
                                </div>
                                <p className="mt-1 text-xs text-blue-600">
                                  <AlertCircle className="h-3 w-3 inline mr-1" />
                                  Duration is auto-calculated from the uploaded video and cannot be edited.
                                </p>
                              </>
                            );
                          })()}
                        </>
                      ) : (
                        <div>
                          <input
                            type="number"
                            value={lessonForm.duration}
                            onChange={(e) => {
                              const val = e.target.value;
                              setLessonForm(prev => ({ ...prev, duration: val }));

                              let error = null;
                              if (val && (isNaN(Number(val)) || Number(val) <= 0)) {
                                error = 'Duration must be greater than 0';
                              } else if (val && Number(val) > 1000) {
                                error = 'Duration must be less than 1000 minutes';
                              }
                              setValidationErrors(prev => ({ ...prev, duration: error }));
                            }}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.duration ? 'border-red-500 bg-red-50' : 'border-gray-300'
                              }`}
                            placeholder="Enter duration in minutes"
                            min="1"
                            max="1000"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Enter the estimated duration of the YouTube video in minutes.
                          </p>
                        </div>
                      )}
                      {validationErrors.duration && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {validationErrors.duration}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Order <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={lessonForm.order}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        // Allow empty string, otherwise parse as integer
                        let value = inputValue === '' ? '' : (parseInt(inputValue) || '');

                        // Prevent entering values beyond max
                        if (value !== '' && value > 1000) {
                          value = 1000;
                        }

                        setLessonForm(prev => ({
                          ...prev,
                          order: value
                        }));

                        // Real-time validation for lesson order
                        let error = null;
                        if (value !== '' && value !== null && value !== undefined) {
                          if (value < 1) {
                            error = 'Order must be at least 1';
                          } else if (value > 1000) {
                            error = 'Order must be less than 1000';
                          }
                        }

                        setValidationErrors(prev => ({ ...prev, order: error }));
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.order ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      min="1"
                      max="1000"
                      placeholder="Enter lesson order"
                    />
                    <div className="mt-1 flex items-center justify-between">
                      {validationErrors.order && (
                        <p className="text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {validationErrors.order}
                        </p>
                      )}
                      {lessonForm.order === 1000 && !validationErrors.order && (
                        <p className="text-xs text-red-600 font-medium ml-auto">
                          Maximum order value (1000) reached
                        </p>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {editingLesson ? 'Editing existing lesson' : 'Creating new lesson'}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    resetLessonForm();
                    setShowAddLesson(false);
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleSaveLesson();
                  }}
                  className="px-6 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors focus:ring-2 focus:ring-agrosaf-green focus:ring-offset-2 flex items-center"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingLesson ? 'Update Lesson' : 'Create Lesson'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Details Modal */}
      {lessonDetailsModal.show && lessonDetailsModal.lesson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Lesson Details</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {lessonDetailsModal.module?.title ? `Module: ${lessonDetailsModal.module.title}` : 'Module details'}
                </p>
              </div>
              <button
                onClick={closeLessonDetailsModal}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Close
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 break-all break-word capitalize">{lessonDetailsModal.lesson.title}</h3>
                  <p className="text-sm text-gray-600 mt-1 break-all break-word first-letter:capitalize">{lessonDetailsModal.lesson.description || 'No description provided'}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-gray-500">Lesson Type</span>
                    <p className="font-medium text-gray-900 capitalize">{lessonDetailsModal.lesson.type || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-gray-500">Order</span>
                    <p className="font-medium text-gray-900">{lessonDetailsModal.lesson.order || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-gray-500">Duration</span>
                    <p className="font-medium text-gray-900">{formatDuration(lessonDetailsModal.lesson.duration || 0)}</p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Content</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap break-all break-word first-letter:capitalize">{lessonDetailsModal.lesson.content || 'No content available'}</p>
                </div>
              </div>

              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-3">Files</h4>
                {Array.isArray(lessonDetailsModal.lesson.files) && lessonDetailsModal.lesson.files.length > 0 ? (
                  <div className="space-y-2">
                    {lessonDetailsModal.lesson.files.map((file, index) => {
                      const fileName = file?.name || `file-${index + 1}`;
                      const fileType = String(file?.type || 'file').toLowerCase();
                      const hasUrl = Boolean(file?.url);
                      const isYoutube = fileType === 'youtube';
                      const showView = isYoutube && hasUrl;
                      const showDownload = !isYoutube && hasUrl;

                      return (
                        <div key={`${fileName}-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center space-x-3 min-w-0">
                            {fileType === 'video' || fileType === 'youtube' ? (
                              <Video className="h-5 w-5 text-blue-600 flex-shrink-0" />
                            ) : (
                              <FileText className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
                              <p className="text-xs text-gray-500 uppercase">
                                {fileType} • {formatFileSize(file?.size)}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 ml-3">
                            {showView && (
                              <button
                                onClick={() => {
                                  window.open(file.url, '_blank', 'noopener,noreferrer');
                                }}
                                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            )}
                            {showDownload && (
                              <button
                                onClick={() => handleDownloadUploadedFile(file.url, fileName)}
                                className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    No files attached to this lesson.
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={closeLessonDetailsModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Delete Confirmation Modal */}
      {fileDeleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Remove File
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Are you sure you want to delete this {fileDeleteConfirm.fileType} file? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeFileDeleteConfirm}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmFileDelete}
                  disabled={deletingFiles[fileDeleteConfirm.fileType]}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingFiles[fileDeleteConfirm.fileType] ? 'Deleting...' : 'Delete File'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Delete Module
                  </h3>
                </div>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete this module? This action will also delete all lessons in this module and cannot be undone.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDeleteModule}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteModule}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Deleting...' : 'Delete Module'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModulesLessons;